import { useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { ArrowLeft, Users, Info, Loader2, ShieldCheck } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useRoomMessages } from '../hooks/useRoomMessages'
import { useRoomMetadata } from '../hooks/useRoomMetadata'
import { RoomMessageBubble } from '../components/Rooms/RoomMessageBubble'
import { RoomMessageInput } from '../components/Rooms/RoomMessageInput'
import { publishRoomMessage } from '../lib/nostr'

export function RoomDetailPage() {
    const { roomId } = useParams<{ roomId: string }>()
    const navigate = useNavigate()
    const { pubkey, sk } = useAuth()
    const { room, isLoading: metadataLoading } = useRoomMetadata(roomId || '')
    const { messages, isLoading: messagesLoading } = useRoomMessages(roomId || '')
    const messagesEndRef = useRef<HTMLDivElement>(null)

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
        if (messages.length > 0) {
            scrollToBottom()
        }
    }, [messages])

    const handleSendMessage = async (content: string) => {
        if (!roomId || !pubkey) return
        try {
            await publishRoomMessage(content, roomId, sk)
        } catch (error) {
            console.error('Failed to send message:', error)
            alert('Mesaj gönderilemedi.')
        }
    }

    if (metadataLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-[60vh] text-gray-400">
                <Loader2 className="w-10 h-10 animate-spin mb-4 text-mavi-500" />
                <p className="font-poppins text-sm animate-pulse">Oda bilgileri yükleniyor...</p>
            </div>
        )
    }

    if (!room) {
        return (
            <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
                <div className="w-20 h-20 bg-surface-100 dark:bg-surface-800 rounded-3xl flex items-center justify-center text-gray-400">
                    <Info className="w-10 h-10" />
                </div>
                <div className="text-center">
                    <h2 className="font-poppins font-bold text-xl text-gray-900 dark:text-white">Oda Bulunamadı</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Aradığınız oda mevcut değil veya henüz senkronize edilmedi.</p>
                </div>
                <button
                    onClick={() => navigate('/rooms')}
                    className="px-6 py-3 bg-mavi-600 text-white rounded-2xl font-poppins font-bold text-sm shadow-lg shadow-mavi-500/20 hover:bg-mavi-700 transition-all"
                >
                    Odalara Dön
                </button>
            </div>
        )
    }

    return (
        <div className="flex flex-col h-[calc(100vh-120px)] bg-white dark:bg-surface-900 rounded-[2.5rem] border border-surface-200 dark:border-surface-700 shadow-sm overflow-hidden">
            {/* Room Header */}
            <div className="p-6 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between bg-surface-50/50 dark:bg-surface-800/30 backdrop-blur-md sticky top-0 z-20">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/rooms')}
                        className="p-3 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-2xl transition-all text-gray-500"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>

                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl overflow-hidden bg-mavi-500/10 flex items-center justify-center text-mavi-500 border border-mavi-500/20">
                            {room.picture ? (
                                <img src={room.picture} alt={room.name} className="w-full h-full object-cover" />
                            ) : (
                                <Users className="w-6 h-6" />
                            )}
                        </div>
                        <div>
                            <h1 className="font-poppins font-bold text-lg text-gray-900 dark:text-white leading-none mb-1">
                                {room.name}
                            </h1>
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1 text-[10px] font-poppins font-bold text-green-500 uppercase tracking-widest">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                    Canlı
                                </div>
                                <span className="text-[10px] text-gray-400 dark:text-gray-500 font-poppins">•</span>
                                <p className="text-[10px] text-gray-400 dark:text-gray-500 font-poppins truncate max-w-[200px]">
                                    {room.about || 'Nostr Grubu'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="hidden md:flex items-center gap-2">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/10 text-orange-500 rounded-xl border border-orange-500/20 text-[10px] font-poppins font-bold uppercase tracking-widest">
                        <ShieldCheck className="w-3 h-3" />
                        Mavililer Onaylı
                    </div>
                </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-2 scroll-smooth custom-scrollbar">
                {messagesLoading && messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 space-y-4">
                        <Loader2 className="w-8 h-8 animate-spin text-mavi-500/50" />
                        <p className="text-xs font-poppins">Mesajlar yükleniyor...</p>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center space-y-4 px-10">
                        <div className="w-16 h-16 bg-surface-50 dark:bg-surface-800 rounded-3xl flex items-center justify-center text-gray-300">
                            <Users className="w-8 h-8" />
                        </div>
                        <div>
                            <h3 className="font-poppins font-bold text-gray-900 dark:text-white">Odaya Hoş Geldin!</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto mt-1">
                                Henüz hiç mesaj yok. İlk mesajı sen göndererek sohbeti başlatabilirsin.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-1">
                        <AnimatePresence initial={false}>
                            {messages.map((msg) => (
                                <RoomMessageBubble
                                    key={msg.id}
                                    event={msg}
                                    isOwnMessage={msg.pubkey === pubkey}
                                />
                            ))}
                        </AnimatePresence>
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>

            {/* Input Area */}
            <div className="p-4 bg-surface-50/50 dark:bg-surface-800/30 backdrop-blur-md border-t border-surface-100 dark:border-surface-800">
                <RoomMessageInput
                    onSend={handleSendMessage}
                    disabled={!pubkey}
                />
                {!pubkey && (
                    <p className="text-center text-[10px] text-gray-500 dark:text-gray-400 font-poppins mt-2">
                        Mesaj göndermek için giriş yapmalısın.
                    </p>
                )}
            </div>
        </div>
    )
}
